/**
 * Webメールの見た目（ブランディング）とカスタムホスト名。
 *
 * **API は 6 本とも実装済みなのに、叩く画面が 1 枚も無かった。**
 * 契約者から見ると「機能があると案内されているのに設定できない」状態で、
 * ヘルプ（`apps/lp/help.html` §6）が案内している「カスタムドメイン」画面が
 * 実在しないという食い違いも起きていた。
 *
 * 画面が守っていること（API 側の規律をそのまま反映する）:
 *   1. **ロゴは自社の公開バケットへ上げたものだけ。** URL 直接入力の欄は作らない
 *      （任意の URL を保存できると、ログイン画面を開いた全員の IP が第三者へ渡る）。
 *      SVG は選ばせない（実行可能なマークアップのため API も受け付けない）。
 *   2. **ホスト名の状態は契約者が変えられない。** `active` にできるのは検証
 *      ジョブだけなので、画面は状態を表示するだけで「有効にする」ボタンを出さない。
 *   3. DNS に入れてもらうのは **CNAME 1 本**（API が `expectedDns` で返す値）。
 *      画面が自前で組み立てない（IP を変えたときに全契約者へ依頼して回ることになる）。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CopyableCode,
  DataTable,
  Field,
  Input,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { Trash2, Upload } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useDomains, verifiedDomains } from '../lib/hooks.js';
import { AppLink } from '../lib/nav.js';
import { Async, ErrorAlert } from '../components/async.js';
import { ConfirmDialogButton } from '../components/confirm-button.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { HelpNote } from '../components/help.js';
import { useAuth, canManageOrg } from '../stores/auth.js';
import type { Branding, CountedList, CustomHostname, LogoUploadTicket } from '../lib/api-types.js';

/** API が受け付ける画像形式。**SVG は含めない**（API 側も拒否する）。 */
const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp';
const LOGO_MAX_BYTES = 512 * 1024;

const KIND_LABEL: Record<CustomHostname['kind'], string> = {
  webmail: 'Webメール',
  link: '配信メールのリンク',
};

export function BrandingPage() {
  const { user } = useAuth();
  const canWrite = canManageOrg(user);

  const branding = useQuery({
    queryKey: qk.branding(),
    queryFn: () => api.get<Branding>('/branding'),
    staleTime: 60_000,
  });

  return (
    <>
      <PageHeader
        title="Webメールの見た目"
        description="Webメールのログイン画面に出る名前・ロゴ・色を設定します。お客様のドメインでの公開もここから申請できます。"
      />

      <div className="space-y-6">
        {!canWrite ? (
          <Alert variant="info">
            <AlertTitle>閲覧のみ可能です</AlertTitle>
            <AlertDescription>設定の変更は組織の管理者のみ行えます。</AlertDescription>
          </Alert>
        ) : null}

        <Async query={branding} lines={5}>
          {(data) => <BrandingForm branding={data} canWrite={canWrite} />}
        </Async>

        <CustomHostnamesSection canWrite={canWrite} />
      </div>
    </>
  );
}

/* ------------------------------------------------------------ ブランディング */

function BrandingForm({ branding, canWrite }: { branding: Branding; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const [brandName, setBrandName] = React.useState(branding.brandName ?? '');
  const [tagline, setTagline] = React.useState(branding.tagline ?? '');
  const [primaryColor, setPrimaryColor] = React.useState(branding.primaryColor ?? '#2563eb');
  const [supportUrl, setSupportUrl] = React.useState(branding.supportUrl ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.put<Branding>('/branding', {
        // 空欄は「未設定に戻す」なので null で送る（空文字を保存しない）。
        brandName: brandName.trim() || null,
        tagline: tagline.trim() || null,
        primaryColor: primaryColor.trim() || null,
        supportUrl: supportUrl.trim() || null,
      }),
    onSuccess: (data) => queryClient.setQueryData(qk.branding(), data),
  });

  const colorInvalid = primaryColor.length > 0 && !/^#[0-9a-fA-F]{6}$/.test(primaryColor.trim());
  const supportInvalid = supportUrl.length > 0 && !supportUrl.trim().startsWith('https://');

  return (
    <Section title="ログイン画面のブランド">
      <Card>
        <CardContent className="p-5">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!colorInvalid && !supportInvalid) save.mutate();
            }}
          >
            <FormAlert error={save.error} />
            {save.isSuccess ? (
              <Alert variant="success">
                <AlertDescription>保存しました。</AlertDescription>
              </Alert>
            ) : null}

            {!branding.configured ? (
              <Alert variant="info">
                <AlertTitle>いまは既定のデザインで表示されています</AlertTitle>
                <AlertDescription>
                  ここで設定すると、Webメールのログイン画面に反映されます。
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="サービス名" htmlFor="br-name" hint="ログイン画面の見出しに出ます。">
                <Input
                  id="br-name"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  disabled={!canWrite}
                  maxLength={80}
                />
              </Field>
              <Field label="ひとこと説明" htmlFor="br-tagline" hint="サービス名の下に小さく出ます。">
                <Input
                  id="br-tagline"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  disabled={!canWrite}
                  maxLength={160}
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="メインの色"
                htmlFor="br-color"
                error={colorInvalid ? '#rrggbb の形式で入力してください' : undefined}
                hint="ボタンやリンクの色になります。"
              >
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="色を選ぶ"
                    className="size-9 shrink-0 cursor-pointer rounded-md border border-input bg-background"
                    value={/^#[0-9a-fA-F]{6}$/.test(primaryColor) ? primaryColor : '#2563eb'}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    disabled={!canWrite}
                  />
                  <Input
                    id="br-color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="font-mono"
                    disabled={!canWrite}
                  />
                </div>
              </Field>

              <Field
                label="サポートページの URL"
                htmlFor="br-support"
                error={supportInvalid ? 'https:// で始まる URL を入力してください' : undefined}
                hint="ログイン画面から「お困りのときは」でリンクします。"
              >
                <Input
                  id="br-support"
                  value={supportUrl}
                  onChange={(e) => setSupportUrl(e.target.value)}
                  placeholder="https://example.com/support"
                  disabled={!canWrite}
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <LogoSlot slot="logo" label="ロゴ" url={branding.logoUrl} canWrite={canWrite} />
              <LogoSlot
                slot="favicon"
                label="ファビコン"
                url={branding.faviconUrl}
                canWrite={canWrite}
              />
            </div>

            {canWrite ? (
              <SubmitBar
                submitLabel="保存する"
                pending={save.isPending}
                disabled={colorInvalid || supportInvalid}
              />
            ) : null}
          </form>
        </CardContent>
      </Card>
    </Section>
  );
}

/**
 * ロゴ / ファビコンの差し替え。
 *
 * 手順は 3 段:
 *   ① `POST /branding/logo` で**アップロード先（presigned PUT）**を払い出す
 *   ② ブラウザから S3 へ直接 PUT する（本文は API を通さない）
 *   ③ `PUT /branding/logo/complete` で object key を確定する
 * ②を飛ばして③だけ呼ぶと、存在しない画像を指したまま保存される。
 */
function LogoSlot({
  slot,
  label,
  url,
  canWrite,
}: {
  slot: 'logo' | 'favicon';
  label: string;
  url: string | null;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [error, setError] = React.useState<unknown>(null);
  const [busy, setBusy] = React.useState(false);

  const upload = React.useCallback(
    async (file: File) => {
      setError(null);
      if (file.size > LOGO_MAX_BYTES) {
        setError(new Error('画像は 512 KB 以下にしてください。'));
        return;
      }
      setBusy(true);
      try {
        const ticket = await api.post<LogoUploadTicket>('/branding/logo', {
          slot,
          contentType: file.type,
          sizeBytes: file.size,
        });
        // presigned URL への直接 PUT。**業務 API ではない**ので `api.ts` を通さない。
        const res = await fetch(ticket.upload.url, {
          method: ticket.upload.method,
          headers: ticket.upload.headers,
          body: file,
          // 署名対象外のヘッダが増えて 403 になる環境があるため Cookie は送らない。
          credentials: 'omit',
        });
        if (!res.ok) throw new Error(`画像のアップロードに失敗しました（${res.status}）`);
        const saved = await api.put<Branding>('/branding/logo/complete', {
          slot,
          objectKey: ticket.objectKey,
        });
        queryClient.setQueryData(qk.branding(), saved);
      } catch (e) {
        setError(e);
      } finally {
        setBusy(false);
      }
    },
    [slot, queryClient],
  );

  const clear = useMutation({
    mutationFn: () => api.put<Branding>('/branding/logo/complete', { slot, objectKey: null }),
    onSuccess: (saved) => queryClient.setQueryData(qk.branding(), saved),
  });

  return (
    <Field label={label} hint="PNG / JPEG / WebP、512 KB まで。SVG は使用できません。">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          {url ? (
            <img
              src={url}
              alt={`${label}のプレビュー`}
              className="size-12 rounded-md border border-border bg-panel object-contain"
            />
          ) : (
            <div className="flex size-12 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
              なし
            </div>
          )}
          {canWrite ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={busy}
                onClick={() => inputRef.current?.click()}
              >
                <Upload aria-hidden />
                画像を選ぶ
              </Button>
              {url ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={clear.isPending}
                  onClick={() => clear.mutate()}
                >
                  取り消す
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={LOGO_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            // 同じファイルを選び直せるよう値を戻す。
            e.target.value = '';
            if (file) void upload(file);
          }}
        />
        {error ? <ErrorAlert error={error} /> : null}
        {clear.isError ? <ErrorAlert error={clear.error} /> : null}
      </div>
    </Field>
  );
}

/* -------------------------------------------------------- カスタムホスト名 */

function CustomHostnamesSection({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const domains = useDomains();
  const [kind, setKind] = React.useState<CustomHostname['kind']>('webmail');
  const [domainId, setDomainId] = React.useState('');
  const [label, setLabel] = React.useState('webmail');

  const list = useQuery({
    queryKey: qk.customHostnames(),
    queryFn: () => api.get<CountedList<CustomHostname>>('/custom-hostnames'),
    // 検証は非同期（DNS 待ち）。確認中のものがある間だけ追いかける。
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((h) => h.status === 'pending' || h.status === 'verifying')
        ? 15_000
        : false,
  });

  const usable = verifiedDomains(domains.data?.items);
  React.useEffect(() => {
    if (!domainId && usable[0]) setDomainId(usable[0].id);
  }, [domainId, usable]);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; hostname: string; status: string }>('/custom-hostnames', {
        kind,
        domainId,
        label: label.trim().toLowerCase(),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.customHostnames() }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del<void>(`/custom-hostnames/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.customHostnames() }),
  });

  const columns: Array<DataTableColumn<CustomHostname>> = [
    {
      id: 'hostname',
      header: 'ホスト名',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-mono text-sm">{row.hostname}</p>
          <p className="text-xs text-muted-foreground">{KIND_LABEL[row.kind] ?? row.kind}</p>
        </div>
      ),
    },
    {
      id: 'status',
      header: '状態',
      width: 'w-40',
      cell: (row) => (
        <div className="space-y-1">
          <StatusBadge
            status={row.status}
            label={
              row.status === 'active'
                ? '利用できます'
                : row.status === 'failed'
                  ? '確認できません'
                  : '確認中'
            }
            size="sm"
            dot
            pulse={row.status === 'verifying'}
          />
          {row.dnsVerifiedAt ? (
            <p className="text-[11px] text-muted-foreground">
              {formatDateTime(row.dnsVerifiedAt)} に確認
            </p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'dns',
      header: 'DNS に追加するレコード',
      cell: (row) =>
        row.expectedDns.value ? (
          <CopyableCode
            value={`${row.expectedDns.name}. IN CNAME ${row.expectedDns.value}.`}
            className="min-w-0"
          />
        ) : (
          /*
            値が返らないのは**運営側の設定（`SERVICE_BASE_DOMAIN`）が未投入**の
            ときだけで、契約者側でできることは何も無い。「サポートへ」だけを
            出すと、契約者は何を伝えればよいのか分からないまま起票することに
            なるので、原因の側を書いて窓口へつなぐ。
          */
          <span className="text-xs text-muted-foreground">
            登録先の設定が準備中のため、まだ値をご案内できません。
            <AppLink
              to="/support"
              className="mx-1 text-primary underline-offset-4 hover:underline"
            >
              サポート
            </AppLink>
            からお問い合わせください。
          </span>
        ),
    },
    {
      id: 'actions',
      header: '',
      width: 'w-24',
      align: 'right',
      cell: (row) =>
        canWrite ? (
          <ConfirmDialogButton
            title={`${row.hostname} を削除します`}
            description="この名前での Webメールは開けなくなります。DNS の CNAME も削除してください。"
            confirmLabel="削除する"
            pending={remove.isPending}
            onConfirm={() => remove.mutate(row.id)}
          >
            <Trash2 aria-hidden />
            削除
          </ConfirmDialogButton>
        ) : null,
    },
  ];

  return (
    <Section title="お客様のドメインで公開する">
      <div className="space-y-4">
        <HelpNote title="先に DNS の設定が必要です">
          追加すると、その名前を DNS に向けていただくための CNAME レコードを表示します。
          レコードを追加すると自動で確認し、証明書の発行まで行います
          （確認が終わるまで「確認中」のままです。反映には最大で数十分かかります）。
        </HelpNote>

        {list.isError ? <ErrorAlert error={list.error} onRetry={() => void list.refetch()} /> : null}

        <DataTable
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={list.isPending}
          emptyTitle="お客様のドメインでの公開は未設定です"
          emptyDescription="設定しない場合は、サービス標準の名前で Webメールをご利用いただけます。"
        />

        {list.data?.items.some((h) => h.status === 'failed') ? (
          <Alert variant="warning">
            <AlertTitle>確認できていないホスト名があります</AlertTitle>
            <AlertDescription>
              DNS の CNAME レコードが表示どおりに設定されているかご確認ください。
              変更直後は反映に時間がかかることがあります。
            </AlertDescription>
          </Alert>
        ) : null}

        {canWrite ? (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">ホスト名を追加する</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (domainId && label.trim()) create.mutate();
                }}
              >
                <FormAlert error={create.error} />

                {usable.length === 0 ? (
                  <Alert variant="warning">
                    <AlertTitle>確認済みのドメインがありません</AlertTitle>
                    <AlertDescription>
                      先に「ドメイン」画面でドメインを追加し、DNS の確認を完了してください。
                    </AlertDescription>
                  </Alert>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="用途" hint="Webメールの画面か、配信メール内のリンクか。">
                    <Select
                      value={kind}
                      onValueChange={(v) => setKind(v as CustomHostname['kind'])}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="webmail">{KIND_LABEL.webmail}</SelectItem>
                        <SelectItem value="link">{KIND_LABEL.link}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="名前（サブドメイン）" htmlFor="ch-label" hint="英小文字・数字・ハイフン。">
                    <Input
                      id="ch-label"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="webmail"
                      className="font-mono"
                    />
                  </Field>

                  <Field label="ドメイン" hint="確認済みのドメインのみ選べます。">
                    <Select value={domainId} onValueChange={setDomainId}>
                      <SelectTrigger>
                        <SelectValue placeholder="選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        {usable.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <SubmitBar
                  submitLabel="追加する"
                  pending={create.isPending}
                  disabled={usable.length === 0 || label.trim().length === 0}
                  hint={
                    domainId && label.trim()
                      ? `${label.trim().toLowerCase()}.${usable.find((d) => d.id === domainId)?.name ?? ''} を追加します`
                      : undefined
                  }
                />
              </form>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </Section>
  );
}
